/*
 * PTP and the vessel APIs send local times as `yyyy-MM-dd'T'HH:mm` strings. They are shown and
 * compared as text, never through Date, so no time zone can shift them.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

/** `2026-10-10T06:30` → `10 Oct 06:30`; anything unreadable is shown as it is. */
export const formatPtpTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const match = DATE_TIME.exec(value.trim());
  if (!match) return value;
  const [, , month, day, hour, minute] = match;
  const date = `${Number(day)} ${MONTHS[Number(month) - 1]}`;
  return hour ? `${date} ${hour}:${minute}` : date;
};

/** `2026-10-12` or `2026-10-12T09:00` → `12 Oct`. */
export const formatPtpDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const match = DATE_TIME.exec(value.trim());
  if (!match) return value;
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]}`;
};

/** `2026-10` → `October 2026`. */
export const formatMonth = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value ?? ''));
  if (!match) return String(value ?? '');
  return `${MONTH_NAMES[Number(match[2]) - 1]} ${match[1]}`;
};

/** The minute a value names, `yyyy-MM-ddTHH:mm`, whatever separator or seconds it carries. */
export const toMinute = (value: string | null | undefined): string =>
  String(value ?? '').trim().replace(' ', 'T').slice(0, 16);

export const sameMinute = (a: string | null | undefined, b: string | null | undefined): boolean =>
  toMinute(a) === toMinute(b);

/** Hours between a `yyyy-MM-ddTHH:mm` time and now, read as local time. */
export const hoursSince = (value: string | null | undefined, now: Date = new Date()): number | null => {
  const match = DATE_TIME.exec(String(value ?? '').trim());
  if (!match) return null;
  const [, year, month, day, hour = '00', minute = '00'] = match;
  const then = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  return (now.getTime() - then.getTime()) / 3_600_000;
};
